import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { ModalWrapper } from "../Modals";
import { APP_NAME } from "../../utils/constants";

/**
 * SPR-025 T-001 — build metadata baked at CRA build time (deploy.yml injects
 * REACT_APP_* for CI builds; they are undefined for a local `npm start`).
 *   REACT_APP_ENV     — "qa" | "prod" (missing → treated as prod)
 *   REACT_APP_VERSION — package.json version
 *   REACT_APP_BUILD   — "<run_number> (<short_sha>)"
 */
const RAW_ENV = process.env.REACT_APP_ENV;
// Non-prod = anything set that isn't "prod". Missing/undefined (local dev,
// prod builds) is treated as production → no QA badge.
const IS_NON_PROD = Boolean(RAW_ENV) && RAW_ENV !== "prod";
const BUILD_INFO = {
  appName: APP_NAME,
  version: process.env.REACT_APP_VERSION || "dev",
  build: process.env.REACT_APP_BUILD || "—",
  // Display label: "Prod" for prod/missing, "QA" (uppercased) otherwise.
  environment: !RAW_ENV || RAW_ENV === "prod" ? "Prod" : RAW_ENV.toUpperCase(),
};

/** Navigation links for the public site. */
const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/menu", label: "Menu" },
  { to: "/events", label: "Events" },
  { to: "/book", label: "Book" },
  { to: "/give", label: "Give" },
  { to: "/partners", label: "Partners" },
];

/** Active class resolver for NavLink. */
const navClass = ({ isActive }) =>
  [
    "font-heading tracking-widest text-sm transition-colors",
    isActive
      ? "text-teal border-b-2 border-teal pb-0.5"
      : "text-charcoal hover:text-teal",
  ].join(" ");

/**
 * PublicLayout — Customer-facing layout with TCSB navigation.
 * Includes responsive hamburger menu for mobile (375px).
 * Wraps all public routes via React Router <Outlet />.
 *
 * SPR-014: the shopping-cart icon/badge was removed — online ordering is
 * disabled at launch (cart/checkout return post-launch via SPR-013).
 */
export default function PublicLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [buildInfoOpen, setBuildInfoOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {/* ── Brand title bar (BLU-006 §4: aluminum pinstripe, teal eyebrow,
            Anton wordmark, rust underline) ── */}
      <header
        className="sticky top-0 z-50 border-b-[3px] border-charcoal shadow-sm"
        data-testid="public-nav"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg,#c9ced2 0 2px,#d4d8db 2px 4px)",
        }}
      >
        {/* Rust underline accent below the charcoal bottom border. */}
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-rust" data-testid="brand-rust-underline" aria-hidden="true" />

        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-16">
          {/* Brand wordmark. The "TWIN CITY" eyebrow is a button that opens the
              build-info modal (SPR-025 T-001); the "SILVER BULLET" wordmark links
              home. They are siblings (not nested) to keep valid, accessible markup. */}
          <div className="flex items-center gap-2 leading-none">
            <div className="leading-none">
              <button
                type="button"
                onClick={() => setBuildInfoOpen(true)}
                className="block font-heading tracking-[0.35em] text-teal text-xs sm:text-sm hover:opacity-90 transition-opacity cursor-pointer text-left"
                aria-label="Twin City — build info"
                aria-haspopup="dialog"
                data-testid="brand-buildinfo-trigger"
              >
                TWIN CITY
              </button>
              <Link
                to="/"
                className="block leading-none hover:opacity-90 transition-opacity"
                aria-label="Twin City Silver Bullet — home"
                data-testid="brand-wordmark"
              >
                <span className="block font-display tracking-wide text-xl sm:text-2xl uppercase">
                  <span className="text-charcoal">SILVER</span>{" "}
                  <span className="text-rust">BULLET</span>
                </span>
              </Link>
            </div>

            {/* QA / non-prod environment badge — never rendered in prod. */}
            {IS_NON_PROD && (
              <span
                className="font-heading tracking-widest text-[10px] uppercase text-cream bg-rust rounded px-1.5 py-0.5"
                data-testid="env-badge"
              >
                {BUILD_INFO.environment}
              </span>
            )}
          </div>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center gap-8"
            aria-label="Main navigation"
          >
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={navClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Hamburger (mobile only) */}
          <div className="flex items-center gap-4">
            {/* Hamburger (mobile only) */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-1 text-charcoal hover:text-teal transition-colors"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="hamburger-button"
            >
              <span className={`block w-5 h-0.5 bg-current transition-transform ${mobileOpen ? "translate-y-2 rotate-45" : ""}`} />
              <span className={`block w-5 h-0.5 bg-current transition-opacity ${mobileOpen ? "opacity-0" : ""}`} />
              <span className={`block w-5 h-0.5 bg-current transition-transform ${mobileOpen ? "-translate-y-2 -rotate-45" : ""}`} />
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <nav
            className="md:hidden bg-cream border-t border-aluminum/30 px-4 py-4 flex flex-col gap-4"
            aria-label="Mobile navigation"
            data-testid="mobile-nav"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={navClass}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* ── Page content ── */}
      <Outlet />

      {/* ── Footer ── */}
      <footer className="mt-auto border-t border-aluminum/30 bg-cream py-6 text-center">
        <p className="font-body text-charcoal/50 text-sm">
          © {new Date().getFullYear()} Twin City Silver Bullet. Winston-Salem, NC.
        </p>
        <Link
          to="/privacy"
          className="font-body text-charcoal/50 hover:text-teal text-sm underline underline-offset-2 transition-colors"
          data-testid="footer-privacy-link"
        >
          Privacy Policy
        </Link>
      </footer>

      {/* ── Build-info modal (SPR-025 T-001) ── */}
      <ModalWrapper open={buildInfoOpen} setOpen={setBuildInfoOpen}>
        <div className="min-w-[16rem]" data-testid="build-info-modal">
          <h2 className="font-heading tracking-widest text-sm text-charcoal mb-3 pr-4">
            BUILD INFO
          </h2>
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 font-body text-sm text-charcoal">
            <dt className="text-charcoal/60">App name</dt>
            <dd data-testid="build-info-appname">{BUILD_INFO.appName}</dd>

            <dt className="text-charcoal/60">Version</dt>
            <dd data-testid="build-info-version">{BUILD_INFO.version}</dd>

            <dt className="text-charcoal/60">Build</dt>
            <dd data-testid="build-info-build">{BUILD_INFO.build}</dd>

            <dt className="text-charcoal/60">Environment</dt>
            <dd data-testid="build-info-environment">{BUILD_INFO.environment}</dd>
          </dl>
        </div>
      </ModalWrapper>
    </div>
  );
}
